import React, { useRef, useState } from "react";

/**
 * ImageMagnifier — Amazon-style hover zoom for product galleries.
 *
 * - Desktop: hovering reveals a zoom lens + 2× magnified view overlaid on
 *   the right half of the image (or full-image overlay on narrow screens).
 *   Pure CSS background-position trick — no extra image requests.
 * - Mobile/touch: disabled (handled by `useTouch`). Native pinch-zoom still
 *   works via the regular <img>.
 */
const ZOOM = 2.5; // 2.5× magnification — sweet spot for fabric/texture detail
const LENS = 140; // px — diameter of the lens that follows the cursor

const ImageMagnifier = ({ src, alt = "" }) => {
  const wrapRef = useRef(null);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 }); // percentages
  const [size, setSize] = useState({ w: 0, h: 0 });

  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
    if (size.w !== rect.width || size.h !== rect.height) {
      setSize({ w: rect.width, h: rect.height });
    }
  };

  const onEnter = (e) => {
    setActive(true);
    onMove(e);
  };

  const onLeave = () => setActive(false);

  // Lens position in pixels (centered on cursor, clamped to image bounds).
  const lensX = (pos.x / 100) * size.w - LENS / 2;
  const lensY = (pos.y / 100) * size.h - LENS / 2;

  return (
    <div
      ref={wrapRef}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative w-full h-full overflow-hidden cursor-zoom-in select-none"
      data-testid="image-magnifier"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="w-full h-full object-cover"
        data-testid="listing-main-image"
      />

      {/* Lens — visible on cursor while hovering. Subtle ring for clarity. */}
      {active && size.w > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute hidden md:block rounded-full border-2 border-white/90 shadow-[0_0_0_2px_rgba(0,0,0,0.15),0_8px_24px_rgba(0,0,0,0.25)] bg-white/10 backdrop-saturate-150"
          style={{
            width: LENS,
            height: LENS,
            transform: `translate(${lensX}px, ${lensY}px)`,
          }}
        />
      )}

      {/* Magnified view — fills the image area for a tactile, full-bleed reveal. */}
      {active && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden md:block transition-opacity duration-150"
          style={{
            opacity: active ? 1 : 0,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${ZOOM * 100}%`,
            backgroundPosition: `${pos.x}% ${pos.y}%`,
          }}
          data-testid="image-magnifier-zoom"
        />
      )}
    </div>
  );
};

export default ImageMagnifier;
